import { useEffect, useState } from 'react'
import { api } from '../api/client'
import type { Material, Project } from '../types'
import { fmtDate, fmtMoney } from '../lib/format'
import Badge from '../components/Badge'

export default function MaterialsTab({ project }: { project: Project }) {
  const [items, setItems] = useState<Material[]>([])
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState<Partial<Material>>({ is_owner_supplied: 1 })

  useEffect(() => {
    api.materials.list(project.id).then(setItems)
  }, [project.id])

  async function add() {
    if (!draft.item) return
    const m = await api.materials.create(project.id, draft)
    setItems((p) => [...p, m])
    setAdding(false)
    setDraft({ is_owner_supplied: 1 })
  }

  async function markArrived(m: Material) {
    const updated = await api.materials.update(m.id, { arrived: 1 } as Partial<Material>)
    setItems((p) => p.map((x) => (x.id === m.id ? updated : x)))
  }

  return (
    <section>
      <div className="row-between mb-3">
        <div>
          <h2>Owner-Supplied + Long-Lead Materials</h2>
          <p className="muted small">
            {items.length} items · {items.filter((m) => m.arrived).length} arrived
          </p>
        </div>
        <button className="btn-primary" onClick={() => setAdding(!adding)}>
          {adding ? 'Cancel' : '+ Add'}
        </button>
      </div>

      <div className="callout small mb-3">
        Subs supply trade materials per their bid. Heritage orders only what's owner-specified
        (Marvin windows, appliances, client-selected fixtures).
      </div>

      {adding && (
        <div className="card mb-3">
          <label className="form-row">
            <span>Item</span>
            <input
              value={draft.item ?? ''}
              onChange={(e) => setDraft({ ...draft, item: e.target.value })}
              placeholder="Marvin windows"
            />
          </label>
          <label className="form-row">
            <span>Vendor</span>
            <input
              value={draft.vendor ?? ''}
              onChange={(e) => setDraft({ ...draft, vendor: e.target.value })}
            />
          </label>
          <label className="form-row">
            <span>Lead time (weeks)</span>
            <input
              type="number"
              value={draft.lead_time_weeks ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, lead_time_weeks: parseInt(e.target.value) || null })
              }
            />
          </label>
          <label className="form-row">
            <span>ETA</span>
            <input
              type="date"
              value={draft.eta_date ?? ''}
              onChange={(e) => setDraft({ ...draft, eta_date: e.target.value })}
            />
          </label>
          <label className="form-row">
            <span>Total amount</span>
            <input
              type="number"
              value={draft.total_amount ?? ''}
              onChange={(e) =>
                setDraft({ ...draft, total_amount: parseFloat(e.target.value) || null })
              }
            />
          </label>
          <button className="btn-primary" onClick={add}>
            Add
          </button>
        </div>
      )}

      <div className="card no-pad">
        <div className="mat-grid mat-hdr">
          <div>Item</div>
          <div>Vendor</div>
          <div>Lead</div>
          <div>ETA</div>
          <div className="text-right">Amount</div>
          <div>Status</div>
        </div>
        {items.map((m) => (
          <div key={m.id} className="mat-grid">
            <div className="bud-item">{m.item}</div>
            <div className="muted small">{m.vendor ?? '—'}</div>
            <div className="muted small">{m.lead_time_weeks ? `${m.lead_time_weeks}w` : '—'}</div>
            <div className="muted small">{m.eta_date ? fmtDate(m.eta_date) : '—'}</div>
            <div className="text-right">{fmtMoney(m.total_amount)}</div>
            <div>
              {m.arrived ? (
                <Badge color="green">ARRIVED</Badge>
              ) : (
                <button className="btn-ghost small" onClick={() => markArrived(m)}>
                  Mark arrived
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
